import React, {useEffect, useState} from "react";

import DataService from "../DataService";
import SearchBar from "../components/search-bar";
import Autocomplete from "@mui/material/Autocomplete";
import {
    Avatar,
    Button,
    IconButton,
    TextField,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
    Typography,
    Box,
    Chip,
    Divider
} from "@mui/material";
import {useLocalStorage} from "usehooks-ts";
import {DataGridPro} from "@mui/x-data-grid-pro";
import {toast} from "react-toastify";
import {OpenInNew} from "@material-ui/icons";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import RefreshIcon from "@mui/icons-material/Refresh";
import JSZip from 'jszip';

const INITIAL_SORT_MODEL = [
    {
        field: 'project',
        sort: 'asc',
    },
    {
        field: 'location',
        sort: 'asc',
    },
]

const PRIORITY_COLOR_MAP = new Map();
PRIORITY_COLOR_MAP.set('5', {color: '#FF0003', blcTxt: false});
PRIORITY_COLOR_MAP.set('4', {color: '#FE5B01', blcTxt: false});
PRIORITY_COLOR_MAP.set('3', {color: '#FCCE03', blcTxt: true});
PRIORITY_COLOR_MAP.set('2', {color: '#CC33AA', blcTxt: false});
PRIORITY_COLOR_MAP.set('1', {color: '#5F13FF', blcTxt: false});
PRIORITY_COLOR_MAP.set('0', {color: '#A3795C', blcTxt: false});
PRIORITY_COLOR_MAP.set('-1', {color: '#D9D9D9', blcTxt: true});
PRIORITY_COLOR_MAP.set('-2', {color: '#B6B6B6', blcTxt: true});
PRIORITY_COLOR_MAP.set('-3', {color: '#8E8E8E', blcTxt: false});
PRIORITY_COLOR_MAP.set('-4', {color: '#5B5B5B', blcTxt: false});
PRIORITY_COLOR_MAP.set('-5', {color: '#1F1F1F', blcTxt: false});;

export default function BreakoutContainer() {
    const [data, setIannData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [reload, setReload] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [stadiumFilter, setStadiumFilter] = useLocalStorage(`stadiumFilter-Breakout}`, [])
    const [projectFilter, setProjectFilter] = useLocalStorage(`projectFilter-Breakout`, [])
    const [stageFilter, setStageFilter] = useLocalStorage(`stageFilter-Breakout`, 'In progress')
    const [sortModel, setSortModel] = useLocalStorage(`iannDataTableSortingModel-Breakout`, INITIAL_SORT_MODEL);

    // Bulk download states
    const [bulkDownloadMode, setBulkDownloadMode] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [showBulkDownloadDialog, setShowBulkDownloadDialog] = useState(false);
    const [emailAddresses, setEmailAddresses] = useState('');
    const [singleFile, setSingleFile] = useState(true);
    const [isSubmittingBulkDownload, setIsSubmittingBulkDownload] = useState(false);

    const projectFilterOptions = Array
        .from(new Set(data.map((el) => el?.project)))
        .map(
            (el, index) => ({id: index, label: el}),
        )
        .sort(
            (a, b) => {
                if (typeof a.label === 'string') {
                    return a?.label?.localeCompare(b?.label)
                }
                return 0
            }
        ) ?? [];
    const stadiumFilterOptions = Array
        .from(
            new Set(
                data
                    .filter((el) => !!projectFilter?.find(
                        (filterP) => filterP.label === el.project,
                    ))
                    .map((el) => el.location),
            ),
        )
        .map(
            (el, index) => ({
                id: index,
                label: el,
            }),
        )
        .sort(
            (a, b) => a.label.localeCompare(b.label)
        )

    // Handle bulk download submission
    const handleBulkDownloadSubmit = async () => {
        if (!emailAddresses.trim()) {
            toast.error('Please provide at least one email address');
            return;
        }

        const emailList = emailAddresses.split(',').map(email => email.trim()).filter(email => email);
        if (emailList.length === 0) {
            toast.error('Please provide at least one valid email address');
            return;
        }

        if (selectedRows.length === 0) {
            toast.error('Please select at least one breakout');
            return;
        }

        if (selectedRows.length > 200) {
            toast.error('Maximum 200 breakouts can be selected');
            return;
        }

        setIsSubmittingBulkDownload(true);
        try {
            const breakoutsHash = selectedRows.map(rowId => {
                const row = data.find(item => item.id === rowId);
                return row?.breakoutId;
            }).filter(Boolean);

            await DataService.bulkDownloadBreakouts({
                breakoutsHash,
                emailAddresses: emailList,
                singleFile
            });

            toast.success('Bulk download request submitted successfully. You will receive an email when the files are ready.');
            setShowBulkDownloadDialog(false);
            setBulkDownloadMode(false);
            setSelectedRows([]);
            setEmailAddresses('');
            setSingleFile(true);
        } catch (error) {
            console.error('Bulk download failed:', error);
            toast.error('Failed to submit bulk download request. Please try again.');
        } finally {
            setIsSubmittingBulkDownload(false);
        }
    };

    // Handle row selection
    const handleRowSelectionChange = (newSelection) => {
        if (bulkDownloadMode) {
            if (newSelection.length > 200) {
                toast.warning('Maximum 200 rows can be selected');
                return;
            }
            setSelectedRows(newSelection);
        }
    };

    // Toggle bulk download mode
    const toggleBulkDownloadMode = () => {
        if (bulkDownloadMode) {
            setBulkDownloadMode(false);
            setSelectedRows([]);
        } else {
            setBulkDownloadMode(true);
        }
    };

    const columns = [
        {
            field: 'priority',
            headerName: 'Priority',
            editable: true,
            type: 'singleSelect',
            width: 70,
            valueOptions: [-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5],
            valueGetter: (params) => params.row.priority,
            renderCell: (params) => {
                const val = params.row.priority;
                let color = '#FBC02D';
                let txt = '#fff';
                const getColor = PRIORITY_COLOR_MAP.get(String(val));
                if (getColor) {
                    color = getColor.color;
                    if (getColor.blcTxt) txt = '#000';
                }
                return (
                    <Avatar
                        sx={{
                            width: 24,
                            height: 24,
                            fontSize: '.9rem',
                            bgcolor: color,
                            color: txt,
                        }}
                    >
                        {params.row.priority}
                    </Avatar>
                );
            },
        },
        {
            field: 'project',
            headerName: 'Project',
            width: 200,
        },
        {
            field: 'location',
            headerName: 'Location',
            width: 200,
            renderCell: ((params) => {
                const {row} = params;
                const {
                    project, location, sessionId, batch,
                } = row;
                const str = `${project} - ${location} - ${sessionId} - ${batch ?? ''}`;
                return (
                    <Button
                        onClick={() => {
                            navigator.clipboard
                                .writeText(str)
                                .then(() => toast.info('Project, location, mediaId, and sessionId copied to clipboard'))
                                .catch(() => toast.error('Error in accessing the clipboard'));
                        }}
                        className="text-primary cursor-pointer decoration-primary decoration-1 underline hover:text-secondary"
                        sx={{
                            textTransform: 'initial',
                        }}
                    >
                        {location}
                    </Button>
                );
            }),
        },
        {
            field: 'feed',
            headerName: 'Feed',
            width: 85,
        },
        {
            field: 'breakoutName',
            headerName: 'Name',
            width: 300,
        },
        {
            field: 'sessionId',
            headerName: 'Session ID',
            width: 120,
            renderCell: (params) => {
                const {row} = params;
                const {sessionId} = row;
                return (
                    <Tooltip title={(
                        <div>
                            <Button
                                target="_blank"
                                href={`https://s3-eu-west-1.amazonaws.com/s3.logograb.com/pub/rendered-media/${sessionId}/output.csv`}
                            >
                                CSV
                            </Button>
                        </div>
                    )}
                    >
            <span
                className="text-success cursor-alias decoration-primary decoration-1 underline hover:text-secondary"
            >
              {sessionId}
            </span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'stage',
            headerName: 'Stage',
        },
        {
            field: 'dateEntered',
            headerName: 'Date submitted (UTC)',
            width: 160,
            type: 'date',
            valueGetter: (params) => new Date(params.row.dateEntered),
            renderCell: (params) => {
                const d = params.row.dateEntered
                if (!d) return '';
                let yourDate = new Date(d);
                return yourDate.toISOString().split('T')[0]
            },
        },
        {
            field: 'rowsNumber',
            headerName: '# Rows',
            width: 100,
        },
        {
            field: 'broadcaster',
            headerName: 'Broadcaster',
            width: 140,
        },
        {
            field: 'fixtureTime',
            headerName: 'Fixture Time',
        },
        {
            field: 'homeTeam',
            headerName: 'Home Team',
            width: 200,
        },
        {
            field: 'visitingTeam',
            headerName: 'Visiting Team',
            width: 200,
        },
        {
            field: 'frameRate',
            headerName: 'Frame Rate',
            type: 'number',
        },
        {
            field: 'resolution',
            headerName: 'Resolution',
        },
        {
            field: 'hours',
            headerName: 'Hours',
            type: 'number',
            width: 80,
        },
        {
            field: 'seasonStage',
            headerName: 'Season Stage',
            width: 140,
        },
        {
            field: 'fixtureType',
            headerName: 'Fixture Type',
        },
        {
            field: 'market',
            headerName: 'Market',
            width: 180,
        },
        {
            field: 'spotRate',
            headerName: 'Spot Rate',
        },
        {
            field: 'mediaUrl',
            headerName: 'Media Url',
            renderCell: params => (
                <IconButton
                    onClick={() => window.open(params.row.mediaUrl, '_blank')}
                    onContextMenu={(e) => {
                        e.preventDefault();
                        if (params.row.mediaUrl.length > 0) {
                            navigator.clipboard.writeText(params.row.mediaUrl)
                                .then(() => toast('Media Url copied to clipboard'))
                                .catch(() => toast.error('Something went wrong'));
                        }
                    }}
                >
                    <OpenInNew/>
                </IconButton>
            )
        },
    ]

    if (stageFilter === 'Completed') {
        const releaseDateStartedColumn = {
            field: 'releaseDateStarted',
            headerName: 'Release Date',
            type: 'date',
            width: 160,
            valueGetter: (params) => params.row.releaseDateStarted ? new Date(params.row.releaseDateStarted) : null,
            renderCell: (params) => {
                const d = params.row.releaseDateStarted;
                if (!d) return '';
                let yourDate = new Date(d);
                return yourDate.toISOString().split('T')[0];
            },
        };

        const downloadedDateColumn = {
            field: 'downloadedDate',
            headerName: 'Downloaded Date',
            type: 'date',
            width: 160,
            valueGetter: (params) => params.row.downloadedDate ? new Date(params.row.downloadedDate) : null,
            renderCell: (params) => {
                const d = params.row.downloadedDate;
                if (!d) return '';
                let yourDate = new Date(d);
                return yourDate.toISOString().split('T')[0];
            },
        };
        
        // Find the index of the 'dateSubmitted' column
        const dateSubmittedIndex = columns.findIndex(col => col.field === 'dateEntered');

        console.log(dateSubmittedIndex);
        // Insert 'release', 'releaseDateStarted', 'downloadedDate' and 'download' right after it
        columns.splice(dateSubmittedIndex + 1, 0, releaseDateStartedColumn, downloadedDateColumn);
        columns.push({
            field: 'download',
            headerName: 'Download',
            renderCell: params => (
                <IconButton
                    onClick={() => window.open(`https://s3-eu-west-1.amazonaws.com/s3.logograb.com/pub/rendered-media/${params.row.sessionId}/${params.row.breakoutId}/output-${params.row.sessionId}-${params.row.breakoutId}.csv`, '_blank')}
                >
                    <DownloadIcon />
                </IconButton>
            )
        });
    }
    useEffect(() => {
        setIsLoading(true);
        DataService.getBreakout(stageFilter === 'In progress' ? 1 : 2)
            .then(resp => {
                const refinedData = resp.map((el, index) => {
                    const media = el.media || {};
                    const developerMeta = media.developerMeta || {};
                    const mediaMeta = media.mediaMeta || {};

                    return {
                        id: index,
                        priority: el.breakout?.priority,
                        project: el.project?.name,
                        breakoutName: el.breakoutGroup?.name ?? 'N/A',
                        location: developerMeta['Fixture Location'] ?? 'N/A',
                        batchSeq: el.task?.batch,
                        sessionId: media.sessionId,
                        breakoutId: el.breakout?.hashId,
                        stage: el.breakout?.status,
                        dateEntered: el.breakout?.dateEntered,
                        rowsNumber: el.breakout?.rowsNumber,
                        fixtureName: developerMeta['Fixture Name'] ?? '',
                        broadcaster: developerMeta.Broadcaster ?? '',
                        fixtureTime: developerMeta['Fixture Time'] ?? '',
                        homeTeam: developerMeta['Home Team'] ?? '',
                        visitingTeam: developerMeta['Visiting Team'] ?? '',
                        frameRate: mediaMeta.fps,
                        resolution: `${mediaMeta.width ?? 'Unknown'}x${mediaMeta.height ?? 'Unknown'}`,
                        seasonStage: developerMeta['Season Stage'] ?? '',
                        fixtureType: developerMeta['Fixture Type'] ?? '',
                        market: developerMeta.Market ?? '',
                        spotRate: developerMeta['Spot Rate'] ?? '',
                        mediaUrl: media.mediaUrl,
                        releaseDateStarted: el.breakout?.meta?.release?.release_date_started,
                        downloadedDate: el.breakout?.meta?.download?.download_date_started,


                        // Calculate hours from frames and fps
                        hours: mediaMeta.frames && mediaMeta.fps
                            ? Math.round((mediaMeta.frames / mediaMeta.fps) / 3600 * 100) / 100
                            : 0,
                        fixtureDate: developerMeta['Fixture Date'] ?? '',
                        sportLeagueId: developerMeta['Sport/League ID'] ?? '',
                        timezone: developerMeta.Timezone ?? '',
                        feed: developerMeta.feed ?? '',
                    };
                });




                setIannData(refinedData);
            })
            .catch(err => setError(err))
            .finally(() => setIsLoading(false))
    }, [reload, stageFilter])

    return (
        <div style={{
            margin: '16px',
            padding: '8px',
            borderRadius: '0.25rem',
            overflow: 'hidden',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            backgroundColor: 'white'
        }}>
            {
                error ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '400px',
                            padding: '32px',
                            textAlign: 'center',
                            backgroundColor: '#fafafa',
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0'
                        }}
                    >
                        <ErrorIcon 
                            sx={{ 
                                fontSize: 64, 
                                color: '#d32f2f',
                                marginBottom: '16px'
                            }} 
                        />
                        <Typography 
                            variant="h4" 
                            component="h2" 
                            gutterBottom
                            sx={{ 
                                color: '#d32f2f',
                                fontWeight: 600
                            }}
                        >
                            Unable to Load Breakout Data
                        </Typography>
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: '#666',
                                maxWidth: '500px',
                                marginBottom: '24px'
                            }}
                        >
                            We encountered an error while trying to load the breakout data. This might be due to a network issue, server problem, or temporary service disruption.
                        </Typography>
                        
                        {error && (
                            <Box
                                sx={{
                                    backgroundColor: '#fff3e0',
                                    border: '1px solid #ffb74d',
                                    borderRadius: '4px',
                                    padding: '12px',
                                    marginBottom: '24px',
                                    maxWidth: '600px',
                                    width: '100%'
                                }}
                            >
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: '#e65100',
                                        fontFamily: 'monospace',
                                        fontSize: '12px',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    Error Details: {error.toString()}
                                </Typography>
                            </Box>
                        )}
                        
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<RefreshIcon />}
                                onClick={() => {
                                    setError(null);
                                    setReload(prev => !prev);
                                }}
                                sx={{ minWidth: '140px' }}
                            >
                                Try Again
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => window.location.reload()}
                                sx={{ minWidth: '140px' }}
                            >
                                Refresh Page
                            </Button>
                        </Box>
                        
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                color: '#999',
                                marginTop: '24px'
                            }}
                        >
                            If the problem persists, please contact support or try again later.
                        </Typography>
                    </Box>
                ) : (
                    <div>
                        <header style={{
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            width: '100%',
                        }}>
                            <SearchBar
                                value={searchText}
                                onSearch={(txt) => setSearchText(txt)}
                                placeholder={'Search by session id'}
                            />
                            <Autocomplete
                                style={{
                                    minWidth: '250px'
                                }}
                                sx={{
                                    ' .MuiInputBase-root': {
                                        height: '40px',
                                    },
                                }}
                                multiple
                                onChange={(_, val) => {
                                    setProjectFilter(val);
                                    if (val.length === 0) {
                                        setStadiumFilter([]);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Projects" variant="standard"/>
                                )}
                                value={projectFilter}
                                options={projectFilterOptions}
                            />
                            <Autocomplete
                                style={{
                                    minWidth: '250px'
                                }}
                                sx={{
                                    '.MuiInputBase-root': {
                                        height: '40px',
                                    },
                                }}
                                disabled={!(!!projectFilter && projectFilter?.length > 0)}
                                multiple
                                onChange={(_, val) => setStadiumFilter(val)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Stadium" variant="standard"/>
                                )}
                                value={stadiumFilter}
                                options={
                                    stadiumFilterOptions
                                }
                            />
                            <Autocomplete
                                style={{
                                    minWidth: '150px'
                                }}
                                sx={{
                                    '.MuiInputBase-root': {
                                        height: '40px',
                                    },
                                }}
                                onChange={(_, val) => {
                                    if (val === 'In progress') {
                                        setBulkDownloadMode(false);
                                        setSelectedRows([]);
                                    }
                                    setStageFilter(val)
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Status" variant="standard"/>
                                )}
                                value={stageFilter}
                                options={
                                    [
                                        'In progress',
                                        'Completed'
                                    ]
                                }
                                disableClearable
                            />

                            <div style={{marginLeft: 'auto'}} />

                            {/* Bulk Download Button for Completed stage */}
                            {
                                stageFilter === 'Completed' && (
                                    <>

                                    <Button
                                        color={bulkDownloadMode ? 'error' : 'primary'}
                                        variant="contained"
                                        onClick={toggleBulkDownloadMode}
                                        size={'small'}

                                    >
                                        {bulkDownloadMode ? 'Undo' : (<span
                                            style={{
                                                display: 'flex',
                                                justifyItems: 'center',
                                            }}
                                        >Bulk <DownloadIcon /></span>)}
                                    </Button>

                                    {/* Confirm Bulk Download Button */}
                                    {bulkDownloadMode && selectedRows.length > 0 && (
                                        <Button
                                            color="success"
                                            variant="contained"
                                            onClick={() => setShowBulkDownloadDialog(true)}
                                            disabled={selectedRows.length > 200}
                                            size={'small'}
                                        >
                                            Confirm <DownloadIcon /> ({selectedRows.length})
                                        </Button>
                                    )}
                                        <Divider orientation={'vertical'} flexItem />
                                    </>
                                )
                            }




                            <Button
                                color='success'
                                variant="contained"
                                sx={{mr: 2}}
                                onClick={() => window
                                    .open(
                                        'https://mybreakout.visua.com/',
                                        '_blank',
                                    )
                                    ?.focus()
                                }
                                size={'small'}
                            >
                                ADD Breakout
                            </Button>
                        </header>
                        <div
                            style={{
                                height: 'calc(100vh - 245px)',
                                minHeight: '340px',
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={!bulkDownloadMode}
                                autoHeight={false}
                                rowHeight={75}
                                columns={columns}
                                rows={data
                                    .filter((el) => {
                                        if (!!projectFilter && projectFilter?.length > 0) {
                                            return !!projectFilter.find(
                                                (filterP) => filterP.label === el.project,
                                            );
                                        }
                                        return true;
                                    })
                                    .filter((el) => {
                                        if (!!stadiumFilter && stadiumFilter?.length > 0) {
                                            return !!stadiumFilter.find(
                                                (filterP) => filterP.label === el.location,
                                            );
                                        }
                                        return true;
                                    })
                                    .filter((el) => {
                                        if (searchText.length === 0) return true;
                                        return String(el.mediaId).indexOf(searchText) !== -1
                                        || el?.sessionId?.indexOf(searchText) !== -1
                                    })
                                }
                                loading={isLoading}
                                pinnedColumns={{left: ['__check__', 'priority', 'project', 'location', 'batch']}}
                                sortModel={sortModel}
                                onSortModelChange={setSortModel}
                                checkboxSelection={bulkDownloadMode}
                                onRowSelectionModelChange={handleRowSelectionChange}
                                rowSelectionModel={selectedRows}
                            />
                        </div>
                    </div>
                )
            }

            {/* Bulk Download Confirmation Dialog */}
            <Dialog
                open={showBulkDownloadDialog}
                onClose={() => setShowBulkDownloadDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                    <span>Bulk Download Confirmation</span>
                    <IconButton
                        onClick={() => setShowBulkDownloadDialog(false)}
                        sx={{ ml: 'auto' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        To download the files for the selected tasks please provide at least one valid email address.
                        All files will be prepared into a single zip file and once ready an email with a download link
                        will be sent to all provided email addresses.
                    </Typography>

                    <TextField
                        fullWidth
                        label="Email Addresses (comma-separated)"
                        variant="outlined"
                        value={emailAddresses}
                        onChange={(e) => setEmailAddresses(e.target.value)}
                        placeholder="email1@example.com, email2@example.com"
                        sx={{ mb: 3 }}
                        helperText="Enter one or more email addresses separated by commas"
                    />

                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Please also select whether you wish to receive one merged file or each task a separate file within the zipped file:
                    </Typography>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={singleFile}
                                onChange={(e) => setSingleFile(e.target.checked)}
                            />
                        }
                        label="Merge the breakouts into a single CSV file"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!singleFile}
                                onChange={(e) => setSingleFile(!e.target.checked)}
                            />
                        }
                        label="Keep the breakout is separate CSV file"
                    />

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Selected Breakouts ({selectedRows.length}):
                        </Typography>
                        <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', p: 1, borderRadius: 1 }}>
                            {selectedRows.map((rowId) => {
                                const row = data.find(item => item.id === rowId);
                                return row ? (
                                    <Chip
                                        key={rowId}
                                        label={`${row.project} - ${row.location} - ${row.breakoutName}`}
                                        size="small"
                                        sx={{ m: 0.5 }}
                                    />
                                ) : null;
                            })}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setShowBulkDownloadDialog(false)}
                        disabled={isSubmittingBulkDownload}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleBulkDownloadSubmit}
                        variant="contained"
                        color="primary"
                        disabled={isSubmittingBulkDownload || !emailAddresses.trim()}
                    >
                        {isSubmittingBulkDownload ? 'Submitting...' : 'Confirm Download'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
